/**
 * Shipments View Screen
 * 
 * Displays shipments assigned to the sales representative.
 * Allows viewing and tracking shipments.
 * 
 * @module ShipmentsViewScreen
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Platform } from '../../components/common';
import {
  Package,
  ExternalLink,
  Search,
  RefreshCw,
  Truck
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Shipment, ShipmentGroup, AssignedShipment } from '../../@types/shipment';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { ShipmentService } from '../../services/ShipmentService';
import { TrackingLinkService } from '../../services/TrackingLinkService';
import { useShipmentNotifications } from '../../hooks/useShipmentNotifications';
import toast, { Toaster } from 'react-hot-toast';
import { Check, Trash2 } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: ${ThemeColors.background.primary};
  padding: ${Spacing.md};
`;

const Header = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${Spacing.md};

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  background: white;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ShipmentCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
`;

const CardTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const Badge = styled.span<{ variant?: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.light;
      case 'warning': return ThemeColors.warning.light;
      default: return ThemeColors.primary.light;
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.dark;
      case 'warning': return ThemeColors.warning.dark;
      default: return ThemeColors.primary.dark;
    }
  }};
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
`;

const CardLabel = styled.span`
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const CardValue = styled.span`
  color: ${ThemeColors.text.primary};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.md};
  border: none;
  background: ${ThemeColors.primary.main};
  color: white;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  width: 100%;
  justify-content: center;
  margin-top: ${Spacing.sm};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemsList = styled.div`
  margin-top: ${Spacing.sm};
  padding-top: ${Spacing.sm};
  border-top: 1px solid ${ThemeColors.border.main};
`;

const ItemRow = styled.div`
  padding: ${Spacing.xs};
  background: ${ThemeColors.background.secondary};
  margin-bottom: ${Spacing.xs};
  border-radius: 4px;
  font-size: ${Typography.fontSize.sm};
`;

export const ShipmentsViewScreen: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Get shipment notifications hook to mark as read
  const { unreadCount, markAllAsRead } = useShipmentNotifications();

  const [shipments, setShipments] = useState<AssignedShipment[]>([]);
  const [shipmentGroups, setShipmentGroups] = useState<ShipmentGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mark notifications as read when viewing shipments
  useEffect(() => {
    if (unreadCount > 0) {
      markAllAsRead();
    }
  }, [unreadCount, markAllAsRead]);

  useEffect(() => {
    loadShipments();
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [shipments, searchQuery]);

  const loadShipments = async () => {
    if (!user) {
      console.warn('[ShipmentsViewScreen] No user found');
      return;
    }

    try {
      setLoading(true);
      console.log('[ShipmentsViewScreen] Loading assigned shipments for user:', user.id);
      
      // Load from assigned_shipments subcollection
      const data = await ShipmentService.getAssignedShipments(user.id);
      setShipments(data);
      
      // Group by tracking ID (convert AssignedShipment[] to Shipment[] for grouping)
      const shipmentData: Shipment[] = data.map(s => ({
        ...s,
        // Remove assigned-specific fields for grouping
      } as Shipment));
      const groups = ShipmentService.groupShipmentsByTrackingId(shipmentData);
      setShipmentGroups(groups);
      
      console.log('[ShipmentsViewScreen] Loaded', data.length, 'assigned shipments in', groups.length, 'groups');
    } catch (err: any) {
      console.error('[ShipmentsViewScreen] Error loading shipments:', err);
      // Check for index-related errors
      if (err.message?.includes('index') || err.code === 'failed-precondition') {
        toast.error('Database index is being built. Please try again in a few minutes.');
      } else if (err.code === 'permission-denied') {
        toast.error('You do not have permission to view shipments.');
      } else {
        toast.error('Failed to load shipments. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchQuery) {
      const shipmentData: Shipment[] = shipments.map(s => ({ ...s } as Shipment));
      const groups = ShipmentService.groupShipmentsByTrackingId(shipmentData);
      setShipmentGroups(groups);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = shipments.filter(s =>
      s.trackingId.toLowerCase().includes(query) ||
      s.shipToName.toLowerCase().includes(query) ||
      s.customerNo.toLowerCase().includes(query) ||
      s.invoiceNo.toLowerCase().includes(query)
    );

    const shipmentData: Shipment[] = filtered.map(s => ({ ...s } as Shipment));
    const groups = ShipmentService.groupShipmentsByTrackingId(shipmentData);
    setShipmentGroups(groups);
  };

  const handleConfirmShipment = async (group: ShipmentGroup) => {
    if (!user) return;

    try {
      setLoading(true);
      // Confirm all shipments in the group
      for (const shipment of group.shipments) {
        // Find the assigned shipment ID
        const assignedShipment = shipments.find(s => 
          s.originalShipmentId === shipment.id || 
          (s.trackingId === shipment.trackingId && s.itemCode === shipment.itemCode)
        );
        
        if (assignedShipment) {
          await ShipmentService.confirmAssignedShipment(user.id, assignedShipment.id);
        }
      }
      
      toast.success('Shipment confirmed');
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentsViewScreen] Error confirming shipment:', err);
      toast.error('Failed to confirm shipment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShipment = async (group: ShipmentGroup) => {
    if (!user) return;
    
    if (!confirm('Are you sure you want to delete this shipment? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      // Delete all shipments in the group
      for (const shipment of group.shipments) {
        // Find the assigned shipment ID
        const assignedShipment = shipments.find(s => 
          s.originalShipmentId === shipment.id || 
          (s.trackingId === shipment.trackingId && s.itemCode === shipment.itemCode)
        );
        
        if (assignedShipment) {
          await ShipmentService.deleteAssignedShipment(user.id, assignedShipment.id);
        }
      }
      
      toast.success('Shipment deleted');
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentsViewScreen] Error deleting shipment:', err);
      toast.error('Failed to delete shipment');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackShipment = (group: ShipmentGroup) => {
    // Always regenerate tracking URL to ensure it's up-to-date (especially for Purolator freight IDs)
    // This ensures we use the latest logic even if the stored URL is outdated
    if (group.trackingId && group.carrier) {
      const url = TrackingLinkService.generateTrackingUrl(group.carrier, group.trackingId);
      if (url) {
        window.open(url, '_blank');
        return;
      }
    }

    // Fallback to stored URL if generation fails
    if (group.trackingUrl) {
      window.open(group.trackingUrl, '_blank');
    } else {
      toast.error('No tracking URL available');
    }
  };

  if (Platform.OS === 'web') {
    return (
      <PageContainer>
        <Toaster position="top-right" />
        <MainContent>
          <Header>
            <Title>My Shipments</Title>
          </Header>

          <SearchBar>
            <Search size={18} />
            <SearchInput
              placeholder="Search shipments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>

          <Button onClick={loadShipments} disabled={loading} style={{ marginBottom: Spacing.md }}>
            <RefreshCw size={16} />
            Refresh
          </Button>

          {loading && shipments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: Spacing.xl }}>
              <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p>Loading shipments...</p>
            </div>
          ) : (
            shipmentGroups.map((group) => (
              <ShipmentCard key={group.trackingId}>
                <CardHeader>
                  <CardTitle>Tracking: {group.trackingId}</CardTitle>
                  <Badge variant="info">
                    {TrackingLinkService.getCarrierDisplayName(group.carrier)}
                  </Badge>
                </CardHeader>
                <CardRow>
                  <CardLabel>Customer No:</CardLabel>
                  <CardValue>{group.shipments[0]?.customerNo || 'N/A'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Customer:</CardLabel>
                  <CardValue>{group.shipToName}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Address:</CardLabel>
                  <CardValue>{group.shipToAddress}, {group.shipToCity}, {group.shipToState}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Ship Date:</CardLabel>
                  <CardValue>{group.shipDate}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Total Items:</CardLabel>
                  <CardValue>{group.totalItems}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Total Quantity Shipped:</CardLabel>
                  <CardValue>{group.totalQuantity}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Total Quantity Ordered:</CardLabel>
                  <CardValue>{group.shipments.reduce((sum, s) => sum + s.quantityOrdered, 0)}</CardValue>
                </CardRow>
                <ItemsList>
                  <strong>Items:</strong>
                  {group.shipments.map((shipment, idx) => {
                    // Use product/supply name from database (fetched by ItemCode), fallback to CSV description only if not found
                    const displayName = shipment.itemNameEN || shipment.itemCodeDesc || shipment.itemCode || 'N/A';
                    return (
                      <ItemRow key={idx}>
                        {displayName} 
                        (Qty: {shipment.quantityShipped})
                      </ItemRow>
                    );
                  })}
                </ItemsList>
                <div style={{ display: 'flex', gap: Spacing.sm, marginTop: Spacing.sm }}>
                  <Button
                    onClick={() => handleTrackShipment(group)}
                    disabled={!group.trackingUrl}
                    style={{ flex: 1 }}
                  >
                    <ExternalLink size={16} />
                    Track Shipment
                  </Button>
                  {group.shipments.some(s => {
                    const assignedShipment = shipments.find(as => 
                      as.originalShipmentId === s.id || 
                      (as.trackingId === s.trackingId && as.itemCode === s.itemCode)
                    );
                    return assignedShipment && !assignedShipment.confirmed;
                  }) && (
                    <Button
                      onClick={() => handleConfirmShipment(group)}
                      disabled={loading}
                      style={{ 
                        flex: 1, 
                        background: ThemeColors.success.main,
                        maxWidth: '150px'
                      }}
                    >
                      <Check size={16} />
                      Confirm
                    </Button>
                  )}
                  <Button
                    onClick={() => handleDeleteShipment(group)}
                    disabled={loading}
                    style={{ 
                      background: ThemeColors.error?.main || '#dc3545',
                      maxWidth: '150px'
                    }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
              </ShipmentCard>
            ))
          )}

          {shipmentGroups.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: Spacing.xl, color: ThemeColors.text.secondary }}>
              <Package size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
              <p>No shipments assigned to you yet.</p>
            </div>
          )}
        </MainContent>
      </PageContainer>
    );
  }

  // Mobile version
  return (
    <Container>
      <Toaster position="top-center" />
      <Header>
        <Title>My Shipments</Title>
      </Header>

      <SearchBar>
        <Search size={18} />
        <SearchInput
          placeholder="Search shipments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchBar>

      <Button onClick={loadShipments} disabled={loading} style={{ marginBottom: Spacing.md }}>
        <RefreshCw size={16} />
        Refresh
      </Button>

      {loading && shipments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: Spacing.xl }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading...</p>
        </div>
      ) : (
        shipmentGroups.map((group) => (
          <ShipmentCard key={group.trackingId}>
            <CardHeader>
              <CardTitle>{group.trackingId}</CardTitle>
              <Badge variant="info">
                {TrackingLinkService.getCarrierDisplayName(group.carrier)}
              </Badge>
            </CardHeader>
            <CardRow>
              <CardLabel>Customer No:</CardLabel>
              <CardValue>{group.shipments[0]?.customerNo || 'N/A'}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Customer:</CardLabel>
              <CardValue>{group.shipToName}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Address:</CardLabel>
              <CardValue>{group.shipToAddress}, {group.shipToCity}, {group.shipToState}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Ship Date:</CardLabel>
              <CardValue>{group.shipDate}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Items:</CardLabel>
              <CardValue>{group.totalItems}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Qty Ordered:</CardLabel>
              <CardValue>{group.shipments.reduce((sum, s) => sum + s.quantityOrdered, 0)}</CardValue>
            </CardRow>
            <CardRow>
              <CardLabel>Qty Shipped:</CardLabel>
              <CardValue>{group.totalQuantity}</CardValue>
            </CardRow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing.sm, marginTop: Spacing.sm }}>
              <Button
                onClick={() => handleTrackShipment(group)}
                disabled={!group.trackingUrl}
              >
                <ExternalLink size={16} />
                Track
              </Button>
              {group.shipments.some(s => {
                const assignedShipment = shipments.find(as => 
                  as.originalShipmentId === s.id || 
                  (as.trackingId === s.trackingId && as.itemCode === s.itemCode)
                );
                return assignedShipment && !assignedShipment.confirmed;
              }) && (
                <Button
                  onClick={() => handleConfirmShipment(group)}
                  disabled={loading}
                  style={{ background: ThemeColors.success.main }}
                >
                  <Check size={16} />
                  Confirm
                </Button>
              )}
              <Button
                onClick={() => handleDeleteShipment(group)}
                disabled={loading}
                style={{ background: ThemeColors.error?.main || '#dc3545' }}
              >
                <Trash2 size={16} />
                Delete
              </Button>
            </div>
          </ShipmentCard>
        ))
      )}

      {shipmentGroups.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: Spacing.xl, color: ThemeColors.text.secondary }}>
          <Package size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
          <p>No shipments found</p>
        </div>
      )}
    </Container>
  );
};

